// Phase 56 chunk 4 — Customer Merge UI.
//
// Side-by-side compare for 2..N customers. Manager picks:
//   * Survivor (radio at column header)
//   * Scalar field winner (radio per row, per column)
//   * List-field union (checkboxes — all items pre-checked so nothing is
//     lost unless the manager actively unchecks)
//
// Live preview shows what the survivor will look like after merge. A
// reassignment-counts summary shows how many bookings/invoices/enquiries
// will move. Final action is a confirm dialog before POST /api/customers/merge.
import { useEffect, useMemo, useState, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, GitMerge, Loader2 } from "lucide-react";

const SCALAR_FIELDS = [
  { key: "company_name", label: "Company name" },
  { key: "contact_name", label: "Contact" },
  { key: "notes",        label: "Notes" },
  { key: "default_payment_type_code", label: "Default payment" },
];

function normalizePhone(p) { return (p || "").replace(/[^\d+]/g, ""); }
function emailKey(e) { return ((e && (e.email || e)) || "").toLowerCase().trim(); }

function dedupeStringList(arr) {
  const seen = new Set();
  const out = [];
  for (const v of arr) {
    const k = (v || "").trim();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

function dedupeEmailList(arr) {
  const seen = new Set();
  const out = [];
  for (const e of arr) {
    const k = emailKey(e);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(typeof e === "string" ? { email: e, receives_invoices: false } : e);
  }
  return out;
}

export default function CustomerMerge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const idsParam = (searchParams.get("ids") || "").trim();
  const ids = useMemo(
    () => idsParam.split(",").map((s) => s.trim()).filter(Boolean),
    [idsParam],
  );
  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Picks state
  const [survivorId, setSurvivorId] = useState(null);
  const [scalarPick, setScalarPick] = useState({});      // {field: customer_id}
  const [scalarOverride, setScalarOverride] = useState({}); // {field: string} - manager free-text override
  const [phoneKeep, setPhoneKeep] = useState({});        // {phoneValue: bool}
  const [emailKeep, setEmailKeep] = useState({});        // {emailValue: bool}
  const [tagKeep, setTagKeep] = useState({});            // {tag_id: bool}

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!ids.length || ids.length < 2) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Fetch each customer + the cluster meta for counts.
        const docs = await Promise.all(
          ids.map((id) => api.get(`/customers/${id}`).then((r) => r.data).catch(() => null)),
        );
        const valid = docs.filter(Boolean);
        if (cancelled) return;
        setCustomers(valid);
        // Pull counts from the first customer's cluster.
        try {
          const r = await api.get(`/customers/${valid[0].id}/duplicate-candidates`);
          if (cancelled) return;
          setCounts(r.data?.cluster?.counts_by_id || {});
        } catch { /* counts are optional */ }
        // Default survivor = first (already sorted bookings DESC server-side).
        if (valid.length) {
          setSurvivorId((cur) => cur || valid[0].id);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [idsParam, ids]);

  // Initialise default picks when customers load / survivor changes.
  useEffect(() => {
    if (!customers.length || !survivorId) return;
    const survivor = customers.find((c) => c.id === survivorId);
    if (!survivor) return;
    setScalarPick((prev) => {
      const next = { ...prev };
      for (const f of SCALAR_FIELDS) {
        if (!next[f.key]) next[f.key] = survivorId;
      }
      return next;
    });
    setPhoneKeep((prev) => {
      const next = { ...prev };
      for (const c of customers) {
        for (const p of (c.phones || [])) {
          const k = normalizePhone(p);
          if (k && !(k in next)) next[k] = true;
        }
      }
      return next;
    });
    setEmailKeep((prev) => {
      const next = { ...prev };
      for (const c of customers) {
        for (const e of (c.emails || [])) {
          const k = emailKey(e);
          if (k && !(k in next)) next[k] = true;
        }
      }
      return next;
    });
    setTagKeep((prev) => {
      const next = { ...prev };
      for (const c of customers) {
        for (const tid of (c.tag_ids || [])) {
          if (!(tid in next)) next[tid] = true;
        }
      }
      return next;
    });
  }, [customers, survivorId]);

  const survivor = customers.find((c) => c.id === survivorId);
  const losers = customers.filter((c) => c.id !== survivorId);

  // Build the survivor preview using the picks.
  const preview = useMemo(() => {
    if (!survivor) return null;
    const findFromPick = (field) => {
      // Manager free-text override beats the radio pick.
      const ov = scalarOverride[field];
      if (typeof ov === "string" && ov.trim()) return ov.trim();
      const pickedId = scalarPick[field] || survivorId;
      const src = customers.find((c) => c.id === pickedId) || survivor;
      return src ? src[field] : null;
    };
    const allPhones = customers.flatMap((c) => c.phones || []);
    const phonesUnion = dedupeStringList(allPhones.filter((p) => phoneKeep[normalizePhone(p)]));
    const allEmails = customers.flatMap((c) => c.emails || []);
    const emailsUnion = dedupeEmailList(allEmails.filter((e) => emailKeep[emailKey(e)]));
    const tagSet = new Set();
    for (const c of customers) {
      for (const tid of (c.tag_ids || [])) {
        if (tagKeep[tid]) tagSet.add(tid);
      }
    }
    return {
      company_name: findFromPick("company_name"),
      contact_name: findFromPick("contact_name"),
      notes:        findFromPick("notes"),
      default_payment_type_code: findFromPick("default_payment_type_code"),
      phones: phonesUnion,
      emails: emailsUnion,
      tag_ids: Array.from(tagSet),
    };
  }, [customers, scalarPick, scalarOverride, phoneKeep, emailKeep, tagKeep, survivorId, survivor]);

  const reassignmentSummary = useMemo(() => {
    let bk = 0, inv = 0, enq = 0;
    for (const c of losers) {
      const cn = counts[c.id] || {};
      bk += Number(cn.bookings || 0);
      inv += Number(cn.invoices || 0);
      enq += Number(cn.enquiries || 0);
    }
    return { bookings: bk, invoices: inv, enquiries: enq };
  }, [losers, counts]);

  const handleSubmit = async () => {
    if (!survivor || !preview || losers.length === 0) return;
    setBusy(true);
    try {
      const body = {
        survivor_id: survivor.id,
        merged_ids: losers.map((c) => c.id),
        merged_fields: {
          company_name: preview.company_name || "",
          contact_name: preview.contact_name || "",
          notes:        preview.notes || "",
          default_payment_type_code: preview.default_payment_type_code || null,
          phones: preview.phones,
          emails: preview.emails,
          tag_ids: preview.tag_ids,
        },
      };
      const r = await api.post("/customers/merge", body);
      const counts = r.data?.reassignment_counts || {};
      toast.success(
        `Merged ${losers.length} customer(s) into ${preview.company_name || survivor.id}. ` +
        `${counts.bookings || 0} bookings, ${counts.invoices || 0} invoices reassigned.`,
      );
      navigate("/customers/duplicates");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setShowConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="page" data-testid="customer-merge-loading">
        <div className="text-muted-foreground text-sm">Loading customers…</div>
      </div>
    );
  }

  if (customers.length < 2) {
    return (
      <div className="page" data-testid="customer-merge-not-enough">
        <div className="bg-amber-50 border border-amber-200 rounded-[8px] p-6">
          <div className="font-bold text-amber-900">Need at least 2 customers to merge</div>
          <div className="text-sm text-amber-800/80 mt-1">
            Open this page from a duplicate cluster on the{" "}
            <a className="underline" href="/customers/duplicates">Find Duplicates</a> page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="customer-merge-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// Merge</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">
            Merge {customers.length} customers
          </h1>
        </div>
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)} data-testid="merge-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto" data-testid="merge-compare-table-wrapper">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className="text-left text-[11px] uppercase tracking-wider text-muted-foreground font-bold p-2 align-bottom w-40">
                Field
              </th>
              {customers.map((c) => {
                const isSurvivor = c.id === survivorId;
                const cn = counts[c.id] || {};
                return (
                  <th
                    key={c.id}
                    className={`text-left p-2 align-bottom border-b-2 ${isSurvivor ? "border-emerald-500" : "border-border"}`}
                  >
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="survivor"
                        checked={isSurvivor}
                        onChange={() => setSurvivorId(c.id)}
                        className="mt-1"
                        data-testid={`merge-survivor-${c.id}`}
                      />
                      <div className="min-w-0">
                        <div className="font-bold text-sm">{c.company_name || c.contact_name || c.id.slice(0, 8)}</div>
                        <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                          {cn.bookings || 0} bk · {cn.invoices || 0} inv · {cn.enquiries || 0} enq
                        </div>
                        {isSurvivor && (
                          <div className="inline-block mono text-[10px] uppercase tracking-widest font-bold bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 mt-1">
                            Survivor
                          </div>
                        )}
                      </div>
                    </label>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {SCALAR_FIELDS.map((f) => {
              const overrideValue = scalarOverride[f.key] || "";
              const hasOverride = overrideValue.trim().length > 0;
              return (
                <Fragment key={f.key}>
                  <tr className={hasOverride ? "" : "border-b border-border"}>
                    <td className="p-2 text-xs font-bold text-muted-foreground align-top">{f.label}</td>
                    {customers.map((c) => {
                      const value = c[f.key] || "";
                      const picked = (scalarPick[f.key] || survivorId) === c.id;
                      return (
                        <td key={c.id} className="p-2 align-top">
                          <label className={`flex items-start gap-2 cursor-pointer ${hasOverride ? "opacity-50" : ""}`}>
                            <input
                              type="radio"
                              name={`scalar-${f.key}`}
                              checked={picked && !hasOverride}
                              onChange={() => setScalarPick((prev) => ({ ...prev, [f.key]: c.id }))}
                              className="mt-1"
                              data-testid={`merge-scalar-${f.key}-${c.id}`}
                              disabled={hasOverride}
                            />
                            <span className={`text-sm ${value ? "text-foreground" : "text-muted-foreground italic"} break-words`}>
                              {value || "(empty)"}
                            </span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="border-b border-border">
                    <td className="p-2 text-[10px] mono uppercase tracking-wider text-muted-foreground align-top">Or custom</td>
                    <td colSpan={customers.length} className="p-2 align-top">
                      <div className="flex items-center gap-2">
                        <Input
                          type={f.key === "notes" ? "text" : "text"}
                          value={overrideValue}
                          placeholder={`Enter a custom ${f.label.toLowerCase()} (overrides selection above)`}
                          onChange={(e) => setScalarOverride((prev) => ({ ...prev, [f.key]: e.target.value }))}
                          className="h-9"
                          data-testid={`merge-scalar-override-${f.key}`}
                        />
                        {hasOverride && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setScalarOverride((prev) => { const n = { ...prev }; delete n[f.key]; return n; })}
                            data-testid={`merge-scalar-override-clear-${f.key}`}
                            title="Clear override and use selection above"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
            {/* Phones — checkbox union */}
            <tr className="border-b border-border">
              <td className="p-2 text-xs font-bold text-muted-foreground align-top">Phones</td>
              {customers.map((c) => (
                <td key={c.id} className="p-2 align-top">
                  <div className="space-y-1">
                    {(c.phones || []).map((p) => {
                      const k = normalizePhone(p);
                      return (
                        <label key={p} className="flex items-center gap-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={!!phoneKeep[k]}
                            onCheckedChange={(v) => setPhoneKeep((prev) => ({ ...prev, [k]: !!v }))}
                            data-testid={`merge-phone-${k}`}
                          />
                          <span>{p}</span>
                        </label>
                      );
                    })}
                    {(!c.phones || c.phones.length === 0) && (
                      <span className="text-xs text-muted-foreground italic">none</span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
            {/* Emails — checkbox union */}
            <tr className="border-b border-border">
              <td className="p-2 text-xs font-bold text-muted-foreground align-top">Emails</td>
              {customers.map((c) => (
                <td key={c.id} className="p-2 align-top">
                  <div className="space-y-1">
                    {(c.emails || []).map((e) => {
                      const k = emailKey(e);
                      const addr = e.email || e;
                      return (
                        <label key={k} className="flex items-center gap-2 cursor-pointer text-sm">
                          <Checkbox
                            checked={!!emailKeep[k]}
                            onCheckedChange={(v) => setEmailKeep((prev) => ({ ...prev, [k]: !!v }))}
                            data-testid={`merge-email-${k}`}
                          />
                          <span className="break-all">{addr}</span>
                        </label>
                      );
                    })}
                    {(!c.emails || c.emails.length === 0) && (
                      <span className="text-xs text-muted-foreground italic">none</span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
            {/* Tags — checkbox union (hydrated tag names if available) */}
            <tr>
              <td className="p-2 text-xs font-bold text-muted-foreground align-top">Tags</td>
              {customers.map((c) => (
                <td key={c.id} className="p-2 align-top">
                  <div className="space-y-1">
                    {(c.tags || []).map((t) => (
                      <label key={t.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                          checked={!!tagKeep[t.id]}
                          onCheckedChange={(v) => setTagKeep((prev) => ({ ...prev, [t.id]: !!v }))}
                          data-testid={`merge-tag-${t.id}`}
                        />
                        <span style={{ color: t.color }}>{t.label}</span>
                      </label>
                    ))}
                    {(!c.tags || c.tags.length === 0) && (
                      <span className="text-xs text-muted-foreground italic">none</span>
                    )}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-[8px] p-4" data-testid="merge-preview">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-emerald-900/70 mb-2">// Survivor preview</div>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-emerald-900/70 font-bold uppercase">Company</div>
            <div className="text-base font-bold">{preview?.company_name || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-emerald-900/70 font-bold uppercase">Contact</div>
            <div>{preview?.contact_name || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-emerald-900/70 font-bold uppercase">Phones</div>
            <div>{preview?.phones?.join(", ") || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-emerald-900/70 font-bold uppercase">Emails</div>
            <div className="break-all">
              {preview?.emails?.map((e) => e.email).join(", ") || "—"}
            </div>
          </div>
        </div>
      </div>

      <div
        className="mt-4 bg-card border border-border rounded-[8px] p-4 flex items-center justify-between gap-4 flex-wrap"
        data-testid="merge-reassignment-summary"
      >
        <div className="text-sm">
          <span className="font-bold">{reassignmentSummary.bookings}</span> bookings,{" "}
          <span className="font-bold">{reassignmentSummary.invoices}</span> invoices,{" "}
          <span className="font-bold">{reassignmentSummary.enquiries}</span> enquiries will move to the survivor.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="merge-cancel">
            Cancel
          </Button>
          <Button
            className="gap-2 bg-red-600 hover:bg-red-700 text-white"
            onClick={() => setShowConfirm(true)}
            disabled={busy || !preview || losers.length === 0}
            data-testid="merge-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
            Merge customers
          </Button>
        </div>
      </div>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent data-testid="merge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge {customers.length} customers?</AlertDialogTitle>
            <AlertDialogDescription>
              This will merge {losers.length} customer{losers.length === 1 ? "" : "s"} into{" "}
              <strong>{preview?.company_name || "the survivor"}</strong>. The merged-out
              customers are soft-deleted and can be restored from the Find Duplicates page
              within 30 days. Invoice snapshots are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="merge-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmit}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="merge-confirm-ok"
            >
              Yes, merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
